import flow from 'lodash/flow';
import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import connectWithPlayer from 'src/common/decorators/connectWithPlayer';
import commonStyles from 'src/common/styles/common';
import playerStatuses from 'src/common/utils/playerStatuses';
import isPlayable from '../../../utils/guideItem/isPlayable';
import isLocked from '../../../utils/guideItem/isLocked';
import isSelectable from '../../../utils/guideItem/isSelectable';
import hasNoBehavior from '../../../utils/guideItem/hasNoBehavior';
import withNowPlayingContext from '../../../decorators/withNowPlayingContext';
import GuideItemLink from './GuideItemLink';
import LockedTile from './LockedTile';
import PlayTile from './PlayTile';
import SelectableTile from './SelectableTile';
import css from './play-button.module.scss';

// exported for testing
export const styles = {
  display: 'block',
  position: 'relative',
  border: commonStyles.borderThin,
  backgroundColor: '#fff',
  overflow: 'hidden',
  borderRadius: '3px',
  cursor: 'pointer',
  zIndex: '1',
  height: '100%',
  width: '100%',
};

export const noAnchorOverrideStyle = {
  cursor: 'default',
};

// exported for testing
export const stylesOverridesMap = {
  Circle: {
    borderRadius: '50%',
  },
};

export class PlayButton extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    guideItem: PropTypes.object.isRequired,
    playerActions: PropTypes.object.isRequired,
    syncedPlayerStatus: PropTypes.string.isRequired,
    syncedPlayerAction: PropTypes.func.isRequired,
    isSyncedWithPlayer: PropTypes.bool.isRequired,
    history: PropTypes.object.isRequired,
    isMobile: PropTypes.bool.isRequired,
    nowPlayingContext: PropTypes.object.isRequired,
    canScrub: PropTypes.bool,
    style: PropTypes.object,
    className: PropTypes.string,
    iconFontSize: PropTypes.string,
    tileSize: PropTypes.string,
    tileShape: PropTypes.string,
    isNowPlayingOpen: PropTypes.bool,
    isClickHandlerEnabled: PropTypes.bool,
    isSelected: PropTypes.bool,
  };

  navigateAndTuneOnPlay = (to) => {
    const { history, guideItem, playerActions } = this.props;
    playerActions.tune({ guideItem });
    // tune() should only be called when isPlayable() is true for the guideItem
    // this check was done below when passing in handleGuideItemLinkClick() as the onClick prop

    history.push(to);
  };

  handleGuideItemLinkClick = (to, event) => {
    event.preventDefault();

    const {
      playerActions,
      syncedPlayerAction,
      isSyncedWithPlayer,
      syncedPlayerStatus,
      canScrub,
      isNowPlayingOpen,
      nowPlayingContext,
    } = this.props;

    // If the current tile has a 'preroll' syncedPlayerStatus
    // (meaning same guideId as player, and a preroll is playing)
    // disable it so there's no double play
    if (syncedPlayerStatus !== playerStatuses.preroll) {
      // when in nowplaying and currently playing item is clicked
      // we want to only scroll and NOT control playback or retune
      if (isSyncedWithPlayer && isNowPlayingOpen) {
        nowPlayingContext.scrollToTop();
        return;
      }

      if (isSyncedWithPlayer) {
        syncedPlayerAction(playerActions, canScrub);
      } else {
        this.navigateAndTuneOnPlay(to);
        if (isNowPlayingOpen) {
          nowPlayingContext.scrollToTop();
        }
      }
    }
  };

  renderContent() {
    const {
      guideItem,
      syncedPlayerStatus,
      children,
      canScrub,
      style,
      className,
      isMobile,
      tileSize,
      tileShape,
      isClickHandlerEnabled,
      isSelected,
    } = this.props;

    const stylesOverrides = stylesOverridesMap[tileShape] || {};
    const combinedStyles = { ...styles, ...stylesOverrides, ...style };

    /* No anchor */
    if (hasNoBehavior(guideItem)) {
      const noAnchorStyle = { ...combinedStyles, ...noAnchorOverrideStyle };
      return (
        <div className={className} style={noAnchorStyle}>
          {children}
        </div>
      );
    }

    /* If should show the upsell dialog */
    if (isLocked(guideItem)) {
      return (
        <LockedTile
          guideItem={guideItem}
          className={className}
          style={combinedStyles}
          isClickHandlerEnabled={isClickHandlerEnabled}
        >
          {children}
        </LockedTile>
      );
    }

    /* If it's playable then anchor so tune begins on click */
    if (isPlayable(guideItem)) {
      return (
        <GuideItemLink
          dataTestId="guideItemLink"
          onClick={this.handleGuideItemLinkClick}
          guideItem={guideItem}
          className={className}
          style={combinedStyles}
        >
          <PlayTile
            syncedPlayerStatus={syncedPlayerStatus}
            canScrub={canScrub}
            isMobile={isMobile}
            tileSize={tileSize}
            tileShape={tileShape}
          />
          {children}
        </GuideItemLink>
      );
    }

    if (isSelectable(guideItem)) {
      return (
        <SelectableTile
          className={className}
          style={{ ...combinedStyles, borderRadius: '6px' }}
          isSelected={isSelected}
        >
          {children}
        </SelectableTile>
      );
    }

    /* Default anchor to browse or profile */
    return (
      <GuideItemLink
        dataTestId="guideItemLink"
        guideItem={guideItem}
        style={combinedStyles}
        className={className}
      >
        {children}
      </GuideItemLink>
    );
  }

  render() {
    return (
      <div
        className={css.playButton}
        data-testid="guideItemPlayButtonContainer"
      >
        {this.renderContent()}
      </div>
    );
  }
}

export default flow(
  connectWithPlayer,
  withRouter,
  withNowPlayingContext,
)(PlayButton);
